import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api, type SyncRun } from "../lib/api";
import { runTone, runReason, isTerminal } from "../lib/run-status";
import {
  RUN_STATUS_GROUPS,
  runStatusGroupFor,
  runStatusLabel,
  type RunStatusGroupKey,
} from "../lib/display-labels";
import { transformDescription, vendorLabel } from "../lib/friendly-labels";
import { useConfirm } from "../components/primitives/Confirm";
import { PageHeader } from "../components/primitives/PageHeader";
import { PageHelp } from "../components/PageHelp";
import { InfoTip } from "../components/primitives/InfoTip";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { EmptyState } from "../components/primitives/EmptyState";

// Activity (spec §6.3): every run, plain words. `RunsView` is the reusable
// body — the Activity page renders it unscoped; the client workspace renders it
// with organizationId (and status="failed" for its Needs attention tab).

const PAGE_LIMIT = 50;

export interface PipelineRef {
  id: string;
  name: string;
  transformId?: string | null;
  sourceInstance?: { connectorType: string } | null;
  destInstance?: { connectorType: string } | null;
  sourceConnectorType?: string;
  destConnectorType?: string;
}

/** Compact UTC timestamp: drops the millisecond + zone noise. */
export function ts(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 19).replace("T", " ");
}

/** A run's direction as vendor names: "Jump → HubSpot". */
function flowOf(p: PipelineRef | undefined): string {
  const src = p?.sourceInstance?.connectorType ?? p?.sourceConnectorType;
  const dst = p?.destInstance?.connectorType ?? p?.destConnectorType;
  return `${vendorLabel(src)} → ${vendorLabel(dst)}`;
}

/**
 * The runs table with its filters, selection and bulk re-run.
 *
 * The whole of Activity lives here so the all-clients page and a client's
 * Activity tab are the same surface rather than two that drift. Each prop
 * locks a filter rather than adding one: a locked client or status is applied
 * and its selector is hidden, because a filter the operator cannot change
 * should not look like one they can.
 */
export function RunsView({
  organizationId,
  status: lockedStatus,
  hidePipelineFilter = false,
  restrictToPipelineIds,
}: {
  organizationId?: string;
  status?: RunStatusGroupKey;
  hidePipelineFilter?: boolean;
  restrictToPipelineIds?: string[];
}) {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pipelineId, setPipelineId] = useState<string>(searchParams.get("pipelineId") ?? "");
  const [group, setGroup] = useState<string>(lockedStatus ?? searchParams.get("status") ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const activeGroup = runStatusGroupFor(lockedStatus ?? group);
  // Every group is one `status` value to the API, "In progress" included: the
  // API resolves that one to its five in-flight statuses before it cuts the
  // page, so no row is filtered away here after the fact.
  const apiStatus = activeGroup?.apiStatus;

  const pipelinesQ = usePolling(() => api.listPipelines(organizationId), 30000);
  const runsQ = usePolling(
    () =>
      api.listSyncLog({
        cursor,
        limit: PAGE_LIMIT,
        pipelineId: pipelineId || undefined,
        organizationId,
        status: apiStatus,
      }),
    5000,
  );

  const allowed = useMemo(
    () => (restrictToPipelineIds ? new Set(restrictToPipelineIds) : null),
    [restrictToPipelineIds],
  );
  const pipelines: PipelineRef[] = useMemo(() => {
    const list: PipelineRef[] = pipelinesQ.data?.pipelines ?? [];
    return allowed ? list.filter((p) => allowed.has(p.id)) : list;
  }, [pipelinesQ.data, allowed]);
  const pipelineById = useMemo(() => {
    const m = new Map<string, PipelineRef>();
    for (const p of pipelinesQ.data?.pipelines ?? []) m.set(p.id, p);
    return m;
  }, [pipelinesQ.data]);

  const rows: SyncRun[] = useMemo(() => {
    const list: SyncRun[] = runsQ.data?.entries ?? [];
    return allowed ? list.filter((r) => allowed.has(r.pipelineId)) : list;
  }, [runsQ.data, allowed]);

  // The 5s poll can move an id out of `rows` (it settled into a different
  // group, or scrolled off the page) without anyone unticking it. Keep the
  // selection in step with what's actually on screen so the bulk bar's count
  // and the ids sent to the API never outlive the row they came from. The
  // updater returns the same Set reference when nothing was stale, so this
  // is a no-op re-render on every poll tick rather than a state change.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const rowIds = new Set(rows.map((r) => r.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (rowIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);

  if (runsQ.status === "loading") return <Pulse />;

  // Any FINISHED run can be re-run, mirroring the API's eligibility rule
  // (RERUNNABLE_STATUSES in apps/api/src/routes/sync-log.ts). A skipped or
  // conflicted run is a decision taken against the config and upstream data of
  // the moment; replaying a batch of them is how an operator applies a later
  // change. Only in-flight runs are withheld, so a replay can't race its own
  // delivery.
  const isRerunnable = (r: SyncRun): boolean => isTerminal(r.status);
  const selectableInView = rows.filter(isRerunnable);
  const selectedInView = selectableInView.filter((r) => selected.has(r.id));
  const allSelected = selectableInView.length > 0 && selectedInView.length === selectableInView.length;
  const someSelected = selectedInView.length > 0;

  /** Add or drop one run from the selection, clearing any previous bulk result. */
  function toggleRow(r: SyncRun) {
    setBulkResult(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.add(r.id);
      return next;
    });
  }
  /** Select or clear every re-runnable row currently on screen. Rows on other
   *  pages are left alone: the header checkbox only speaks for what is visible. */
  function toggleAll() {
    setBulkResult(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) for (const r of selectableInView) next.delete(r.id);
      else for (const r of selectableInView) next.add(r.id);
      return next;
    });
  }

  /** Confirm and re-run the selected events, then report how many went through. */
  async function runBulk() {
    // Scope to rows currently on screen — `selected` can carry an id that has
    // since aged out of `rows` (a poll landed between the tick and the click).
    // What the modal shows and what gets sent must be exactly this list.
    const chosen = rows.filter((r) => selected.has(r.id));
    if (chosen.length === 0) return;
    const ids = chosen.map((r) => r.id);
    const n = ids.length;
    const noun = n === 1 ? "run" : "runs";
    const ok = await confirm({
      tier: 1,
      title: `Re-run ${n} ${noun}?`,
      body:
        n === 1
          ? "Anansi will send this event through its pipeline again. The record in the destination may be created or updated."
          : `Anansi will send these ${n} events through their pipelines again. Records in the destination may be created or updated.`,
      items: chosen.map((r) => ({
        label: pipelineById.get(r.pipelineId)?.name ?? r.pipelineId.slice(0, 8),
        meta: r.eventId,
      })),
      confirmLabel: `Re-run ${n} ${noun}`,
      onConfirm: async () => {
        const { accepted, rejected } = await api.rerunBulk(ids);
        setBulkResult(
          rejected === 0
            ? `Re-running ${accepted} ${accepted === 1 ? "run" : "runs"}. Recovered rows appear below as they finish.`
            : `Re-running ${accepted}; ${rejected} skipped (not eligible or missing their captured event).`,
        );
      },
    });
    if (ok) setSelected(new Set());
  }

  const columns: Column<SyncRun>[] = [
    {
      key: "pipeline",
      label: "PIPELINE",
      render: (r) => {
        const p = pipelineById.get(r.pipelineId);
        return (
          <div>
            <a
              className="run-link"
              href={`/pipelines/${r.pipelineId}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                nav(`/pipelines/${r.pipelineId}`);
              }}
            >
              {p?.name ?? r.pipelineId.slice(0, 8)}
            </a>
            <div className="run-sub">{transformDescription(p?.transformId)}</div>
          </div>
        );
      },
    },
    { key: "flow", label: "FLOW", render: (r) => flowOf(pipelineById.get(r.pipelineId)) },
    {
      key: "record",
      label: "RECORD",
      mono: true,
      render: (r) => <span title={`event ${r.eventId}`}>{r.objectType} · {r.operation}</span>,
    },
    {
      key: "status",
      label: "STATUS",
      render: (r) => (
        <span className="run-status-cell">
          <Badge tone={runTone(r.status)}>{runStatusLabel(r.status)}</Badge>
          {r.supersededByRerun && r.status !== "completed" ? (
            <span className="run-superseded" title="A later re-run of this event synced — this one has been recovered.">
              <Badge tone="ok">Recovered</Badge>
            </span>
          ) : null}
        </span>
      ),
    },
    { key: "when", label: "WHEN", mono: true, render: (r) => ts(r.receivedAt) },
    {
      key: "reason",
      label: "REASON",
      render: (r) => {
        const reason = runReason(r);
        return reason ? (
          <span className="run-reason" title={reason}>{reason}</span>
        ) : (
          <span className="run-reason run-reason--empty">—</span>
        );
      },
    },
  ];

  /** Apply a filter change: reset pagination and the selection, and write the
   *  choice into the URL so the view stays shareable. Other params (the client
   *  workspace's `?env=`) are preserved. */
  function onFilterChange(next: { pipelineId?: string; group?: string }) {
    const nextPipeline = next.pipelineId ?? pipelineId;
    const nextGroup = next.group ?? group;
    if (next.pipelineId !== undefined) setPipelineId(next.pipelineId);
    if (next.group !== undefined) setGroup(next.group);
    setCursor(undefined);
    setSelected(new Set());
    setBulkResult(null);
    const params = new URLSearchParams(searchParams);
    if (nextPipeline) params.set("pipelineId", nextPipeline);
    else params.delete("pipelineId");
    if (!lockedStatus) {
      if (nextGroup) params.set("status", nextGroup);
      else params.delete("status");
    }
    setSearchParams(params, { replace: true });
  }

  return (
    <div>
      <div className="runs-filters">
        {!hidePipelineFilter ? (
          <label className="runs-filter">
            <span className="uppercase-label">Pipeline</span>
            <select
              className="field__input"
              aria-label="Pipeline"
              value={pipelineId}
              onChange={(e) => onFilterChange({ pipelineId: e.target.value })}
            >
              <option value="">All pipelines</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
        ) : null}
        {!lockedStatus ? (
          <label className="runs-filter">
            <span className="uppercase-label">Status</span>
            <select
              className="field__input"
              aria-label="Status"
              value={group}
              onChange={(e) => onFilterChange({ group: e.target.value })}
            >
              <option value="">All statuses</option>
              {RUN_STATUS_GROUPS.map((g) => (
                <option key={g.key} value={g.key}>{g.label}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="runs-bulkbar">
        <div className="runs-bulkbar__left">
          {selected.size > 0 ? (
            <>
              <Button onClick={() => void runBulk()}>{`Re-run ${selected.size} selected`}</Button>
              <Button variant="ghost" onClick={() => { setSelected(new Set()); setBulkResult(null); }}>
                Clear
              </Button>
            </>
          ) : (
            <span className="runs-bulkbar__hint">Tick finished runs to re-run them together.</span>
          )}
        </div>
        {bulkResult ? <span className="runs-bulkbar__result">{bulkResult}</span> : null}
      </div>

      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav(`/activity/${r.id}`)}
        empty={<EmptyState title={lockedStatus === "failed" ? "Nothing failed." : "No runs yet."} />}
        selection={{
          isSelectable: isRerunnable,
          isSelected: (r) => selected.has(r.id),
          onToggle: toggleRow,
          onToggleAll: toggleAll,
          allSelected,
          someSelected,
        }}
      />

      <div className="runs-pager">
        <span className="runs-pager__hint">
          Showing the latest {rows.length} {rows.length === 1 ? "run" : "runs"}.
        </span>
        {runsQ.data?.nextCursor ? (
          <Button variant="secondary" onClick={() => setCursor(runsQ.data!.nextCursor!)}>
            Older
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Activity across every client (spec §6.2) — `RunsView` with nothing locked. */
export function Activity() {
  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            All clients
            <InfoTip termKey="activity" />
          </>
        }
        title="Activity"
      />
      <PageHelp />
      <RunsView />
    </div>
  );
}
